// src/pages/Login/LoginPage.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaEnvelope,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaShieldAlt,
  FaSignInAlt,
  FaUserPlus,
} from "react-icons/fa";

import backgroundImage from "./HeaderSlider_02.jpg";
import { useUser } from "./../Home/UserContext";
import { loginUser } from "./../../services/userServices";
import { getSettings } from "./../../services/settingsServices";
import { toAbsolutePhotoUrl } from "./../../services/settingsPhotoServices";
import useScrollToTop from "./../../hooks/useScollToTop";

const DEFAULT_SETTINGS = {
  nom_club: "MonAppliClub",
  logo_club: "",
  header_url1: "",
  header_url2: "",
  header1_photo_url: "",
  header2_photo_url: "",
  couleur_principale: "",
  primary_color: "",
};

const FALLBACK_LOGO = "/img/logo_club_512.png";
const FALLBACK_MONAPPLICLUB_LOGO = "/img/logos/logo_monappliclub.png";
const LOGIN_REDIRECT_PATH = "/profile-judoka";

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function LoginPage() {
  useScrollToTop();

  const navigate = useNavigate();
  const { updateUser } = useUser();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted || !clubSettings) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...clubSettings,
        });

        const primaryColor =
          clubSettings.couleur_principale || clubSettings.primary_color;

        if (primaryColor) {
          document.documentElement.style.setProperty(
            "--primary-color",
            primaryColor
          );
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const logoUrl = useMemo(() => {
    const logo = toAbsolutePhotoUrl(settings.logo_club?.trim?.() || "");
    return logo || FALLBACK_LOGO;
  }, [settings.logo_club]);

  const backgroundUrl = useMemo(() => {
    const candidate =
      settings.header_url2 ||
      settings.header_url1 ||
      settings.header2_photo_url ||
      settings.header1_photo_url;

    return toAbsolutePhotoUrl(candidate?.trim?.() || "") || backgroundImage;
  }, [
    settings.header_url1,
    settings.header_url2,
    settings.header1_photo_url,
    settings.header2_photo_url,
  ]);

  const canSubmit = useMemo(() => {
    return (
      !isSubmitting &&
      email.trim().length > 4 &&
      password.trim().length >= 1
    );
  }, [email, password, isSubmitting]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) return;

    setErrorMessage("");
    setIsSubmitting(true);

    try {
      const cleanEmail = email.trim().toLowerCase();
      const data = await loginUser(cleanEmail, password);

      if (!data?.userInfo || !data?.token) {
        setErrorMessage("Email ou mot de passe incorrect. Veuillez réessayer.");
        return;
      }

      const safeUserInfo = {
        ...data.userInfo,
        email: data.userInfo?.email || data.userInfo?.mail || cleanEmail,
      };

      localStorage.setItem("token", data.token);
      localStorage.setItem("userInfo", JSON.stringify(safeUserInfo));

      updateUser(safeUserInfo);

      navigate(LOGIN_REDIRECT_PATH);
    } catch (error) {
      console.error("Erreur de connexion :", error);

      setErrorMessage(
        error?.response?.data?.message ||
          "Impossible de se connecter pour le moment. Veuillez réessayer plus tard."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-slate-950 text-white">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url("${backgroundUrl}")`,
        }}
      />

      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/92 via-slate-950/72 to-slate-950/60" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />

      <div className="absolute left-[-120px] top-24 h-80 w-80 rounded-full bg-primary/25 blur-3xl" />
      <div className="absolute bottom-[-140px] right-[-100px] h-96 w-96 rounded-full bg-blue-500/20 blur-3xl" />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10 sm:px-6 lg:px-8">
        <div className="w-full max-w-6xl">
          <div className="mb-6 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-bold text-white/80 backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white hover:text-slate-950"
            >
              <FaArrowLeft className="text-xs" />
              Retour
            </button>

            <Link
              to="/"
              className="hidden items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2.5 text-sm font-bold text-white/80 backdrop-blur-xl transition hover:-translate-y-0.5 hover:bg-white hover:text-slate-950 sm:inline-flex"
            >
              Accueil
            </Link>
          </div>

          <div className="grid overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/10 shadow-2xl backdrop-blur-2xl lg:grid-cols-[0.9fr_1.1fr]">
            <aside className="relative hidden overflow-hidden bg-slate-950/75 p-10 lg:block">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.30),transparent_36%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.20),transparent_36%)]" />

              <div className="relative flex h-full flex-col justify-between">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-white/70 backdrop-blur-xl">
                    <FaShieldAlt className="text-primary" />
                    Espace sécurisé
                  </div>

                  <h1 className="mt-7 font-barlow text-5xl font-bold leading-tight text-white">
                    Connectez-vous à votre espace club.
                  </h1>

                  <p className="mt-5 max-w-md text-base leading-8 text-white/70">
                    Retrouvez vos inscriptions, messages, notifications et
                    fonctionnalités réservées aux adhérents et responsables du
                    club.
                  </p>
                </div>

                <div className="rounded-[2rem] border border-white/10 bg-white/10 p-5 backdrop-blur-xl">
                  <div className="flex items-center gap-4">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white p-2 shadow-xl">
                      <img
                        src={logoUrl}
                        alt={settings.nom_club || "MonAppliClub"}
                        className="h-full w-full object-contain"
                        draggable="false"
                        onError={(event) => {
                          event.currentTarget.src = FALLBACK_MONAPPLICLUB_LOGO;
                        }}
                      />
                    </div>

                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/45">
                        Site officiel
                      </p>

                      <p className="mt-1 truncate font-barlow text-2xl font-bold text-white">
                        {settings.nom_club || "MonAppliClub"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </aside>

            <section className="relative bg-white p-6 text-slate-950 dark:bg-slate-900 dark:text-white sm:p-8 lg:p-10">
              <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />

              <div className="relative mx-auto max-w-md">
                <div className="mb-8 text-center">
                  <div className="mx-auto flex h-24 w-24 items-center justify-center overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-3 shadow-xl dark:border-white/10 dark:bg-white">
                    <img
                      src={logoUrl}
                      alt={settings.nom_club || "MonAppliClub"}
                      className="h-full w-full object-contain"
                      draggable="false"
                      onError={(event) => {
                        event.currentTarget.src = FALLBACK_MONAPPLICLUB_LOGO;
                      }}
                    />
                  </div>

                  <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-orange-700 dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
                    <FaSignInAlt />
                    Connexion
                  </div>

                  <h2 className="mt-5 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white">
                    Bienvenue
                  </h2>

                  <p className="mt-3 text-sm leading-6 text-slate-500 dark:text-slate-400">
                    Accédez à votre espace personnel{" "}
                    <span className="font-bold text-slate-700 dark:text-slate-200">
                      {settings.nom_club || "MonAppliClub"}
                    </span>
                    .
                  </p>
                </div>

                {errorMessage && (
                  <div
                    className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold leading-6 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300"
                    role="alert"
                  >
                    <div className="flex items-start gap-3">
                      <FaExclamationTriangle className="mt-1 shrink-0" />
                      <span>{errorMessage}</span>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label
                      htmlFor="email"
                      className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
                    >
                      Email
                    </label>

                    <div className="relative">
                      <FaEnvelope className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                      <input
                        id="email"
                        type="email"
                        placeholder="votre@email.fr"
                        className={cn(
                          "h-[52px] w-full rounded-2xl border py-3 pl-11 pr-4 text-sm font-semibold outline-none transition",
                          "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400",
                          "focus:border-primary focus:ring-4 focus:ring-primary/10",
                          "dark:border-white/10 dark:bg-slate-950/70 dark:text-white dark:placeholder:text-slate-500"
                        )}
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value);
                          if (errorMessage) setErrorMessage("");
                        }}
                        required
                        autoComplete="email"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="password"
                      className="mb-2 block text-sm font-bold text-slate-700 dark:text-slate-200"
                    >
                      Mot de passe
                    </label>

                    <div className="relative">
                      <FaLock className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Votre mot de passe"
                        className={cn(
                          "h-[52px] w-full rounded-2xl border py-3 pl-11 pr-14 text-sm font-semibold outline-none transition",
                          "border-slate-200 bg-white text-slate-900 placeholder:text-slate-400",
                          "focus:border-primary focus:ring-4 focus:ring-primary/10",
                          "dark:border-white/10 dark:bg-slate-950/70 dark:text-white dark:placeholder:text-slate-500"
                        )}
                        value={password}
                        onChange={(event) => {
                          setPassword(event.target.value);
                          if (errorMessage) setErrorMessage("");
                        }}
                        required
                        autoComplete="current-password"
                      />

                      <button
                        type="button"
                        onClick={() => setShowPassword((value) => !value)}
                        className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-primary dark:hover:bg-white/10"
                        aria-label={
                          showPassword
                            ? "Masquer le mot de passe"
                            : "Afficher le mot de passe"
                        }
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={!canSubmit}
                    className={cn(
                      "inline-flex w-full items-center justify-center gap-3 rounded-full px-6 py-4 text-sm font-bold text-white shadow-lg transition",
                      "bg-primary hover:-translate-y-0.5 hover:brightness-110",
                      "focus:outline-none focus:ring-4 focus:ring-primary/20",
                      "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:brightness-100"
                    )}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Connexion...
                      </>
                    ) : (
                      <>
                        <FaSignInAlt />
                        Se connecter
                      </>
                    )}
                  </button>

                  <div className="flex flex-col gap-3 pt-1 text-sm sm:flex-row sm:items-center sm:justify-between">
                    <Link
                      to="/forgot-password"
                      className="font-bold text-primary transition hover:underline"
                    >
                      Mot de passe oublié ?
                    </Link>

                    <Link
                      to="/register"
                      className="inline-flex items-center gap-2 font-bold text-slate-600 transition hover:text-primary dark:text-slate-300"
                    >
                      <FaUserPlus className="text-xs" />
                      Créer un compte
                    </Link>
                  </div>
                </form>

                <p className="mt-8 text-center text-xs leading-5 text-slate-400">
                  En vous connectant, vous accédez à un espace réservé aux
                  utilisateurs du club.
                </p>
              </div>
            </section>
          </div>

          <p className="mt-5 text-center text-xs font-semibold text-white/70">
            MonAppliClub • Gestion de club simplifiée
          </p>
        </div>
      </div>
    </main>
  );
}

export default LoginPage;